import { PreSignedStoredObject } from "@/features/types";
import useFetcher from "@/utils/use-fetcher";
import useYWR, { YWRKey } from "@/features/ui/hooks/use-ywr";
import {
  calcUrl,
  SWRObjectKey,
  withPreSignedFetcher,
} from "@/features/ui/helpers/fetcher-helpers";
import { UrlParser } from "@/features/ui/helpers/url-parser";
import { StringTransformer } from "@/features/ui/helpers/string-transformer";
import { useMemo } from "react";
import { isErrorResponse } from "@/utils/use-fetcher";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

export interface DownloadUnoFileRequest {}

export interface DownloadUnoFileResponse extends PreSignedStoredObject {}

export interface DownloadUnoFilePathParameter {
  unoFileId: number;
}

export const downloadUnoFileTemplateUrl = (() => {
  let url = "";
  url += "/uno-files/:unoFileId/presign";
  return url;
})();

export const downloadUnoFileKey: YWRKey<DownloadUnoFilePathParameter> = {
  url: (parameter) => {
    return calcUrl(downloadUnoFileTemplateUrl, parameter);
  },
  method: "post",
};

export function parseDownloadUnoFileKey(
  key: SWRObjectKey<DownloadUnoFileRequest>
): {
  parameter: DownloadUnoFilePathParameter;
  request: DownloadUnoFileRequest;
} {
  const urlParser = new UrlParser<DownloadUnoFilePathParameter>(
    downloadUnoFileTemplateUrl,
    {
      unoFileId: StringTransformer.parseId,
    }
  );
  return {
    parameter: urlParser.parse(key.url),
    request: key.params!!,
  };
}

// 같은 압축파일을 하루 한도(서버 기준 3번)만큼 이미 받았을 때 서버가 주는 사유.
// 다운로드 버튼들은 실패를 따로 안내하지 않아(대부분 결과를 기다리지 않는다) 여기서 한 곳에서 알린다.
const SAME_FILE_DAILY_LIMIT_REASON = "SAME_FILE_DAILY_LIMIT";

interface LimitDetail {
  reason?: string;
  metadata?: { limit?: number };
}

export function findSameFileDailyLimit(error: unknown): LimitDetail | null {
  if (!isErrorResponse(error)) return null;
  const details = (error.error.details ?? []) as LimitDetail[];
  return details.find((d) => d.reason === SAME_FILE_DAILY_LIMIT_REASON) ?? null;
}

// 다운로드 fetcher 를 감싸 한도 초과면 안내 토스트를 띄운다. 에러는 그대로 다시 던진다(호출부 동작 유지).
export function useDownloadLimitNotice() {
  const { t } = useI18nHelper();
  const { warning } = useAppToasts({ id: "DOWNLOAD_LIMIT" });
  return useMemo(
    () =>
      <F extends (...args: any[]) => Promise<any>>(fetcher: F): F =>
        (async (...args: Parameters<F>) => {
          try {
            return await fetcher(...args);
          } catch (error) {
            const limit = findSameFileDailyLimit(error);
            if (limit) {
              warning(
                t("download_limit.same_file_daily", {
                  limit: limit.metadata?.limit ?? 3,
                })
              );
            }
            throw error;
          }
        }) as F,
    [t, warning]
  );
}

export default function useDownloadUnoFile() {
  const vanillaFetcher = useFetcher<
    DownloadUnoFileRequest,
    DownloadUnoFileResponse
  >();
  const withLimitNotice = useDownloadLimitNotice();
  const preSignedFetcher = useMemo(() => {
    return withLimitNotice(withPreSignedFetcher(vanillaFetcher));
  }, [vanillaFetcher, withLimitNotice]);

  return useYWR<
    DownloadUnoFilePathParameter,
    DownloadUnoFileRequest,
    DownloadUnoFileResponse
  >(downloadUnoFileKey, preSignedFetcher);
}
